import * as React from 'react'
import { Box, Collapse, Typography, Checkbox, FormControlLabel } from '@mui/material'
import FirstSection from './Organization/FirstSection'
import SectionHeader from './SectionHeader'
import InfoIcon from '@assets/Settings/person.svg'
import GreenUpload from '@assets/Settings/GreenUpload.svg'
import InfoIcon2 from '@assets/Settings/Info.svg'
import ConsumersIcon from '@assets/siderbar_icons/Consumers.svg'
import AlertBellIcon from '@assets/Component 2.svg'
import SmsIcon from '@assets/chanellsIcons/SmsIcon.svg'
import { useAppTheme } from '@/theme/ThemeContext'
import UpdateConsumersModal from './Organization/UpdateConsumersModal'
import { useAlertIndicationStore } from '@/store/notification/alertIndicationStore'

import UsersPermissionsTable from './Organization/UsersPermissionsTable'
import InterfaceStatusTable from './Organization/InterfaceStatusTable'
import SenderNamePanel from './Organization/SenderNamePanel'

type PanelKey = 'orgName' | 'senderName' | 'usersPermissions' | 'interfaceStatus' | 'alertIndication' | null

const Organization = () => {
  const [openPanel, setOpenPanel] = React.useState<PanelKey>('orgName')
  const { mode } = useAppTheme()
  const [isUpdateModalOpen, setIsUpdateModalOpen] = React.useState(false)
  const isDarkMode = mode === 'dark'
  const BORDER = isDarkMode ? '#374151' : '#E4E7EB'

  const { showMutedAlertIndication, setShowMutedAlertIndication } = useAlertIndicationStore()

  const toggle = (key: PanelKey) => {
    setOpenPanel(prev => (prev === key ? null : key))
  }

  return (
    <Box sx={{ direction: 'rtl' }}>
      <Box py="20px" sx={{ borderBottom: `1px solid ${BORDER}` }}>
        <SectionHeader
          title="שם ותמונת אתר"
          icon={InfoIcon2}
          isOpen={openPanel === 'orgName'}
          onExpandClick={() => toggle('orgName')}
        />
        <Collapse in={openPanel === 'orgName'} unmountOnExit>
          <Box pt={2}>
            <FirstSection />
          </Box>
        </Collapse>
      </Box>

      {/* SMS Sender Name Section */}
      <Box py="20px" sx={{ borderBottom: `1px solid ${BORDER}` }}>
        <SectionHeader
          title="שם השולח (SMS)"
          icon={SmsIcon}
          isOpen={openPanel === 'senderName'}
          onExpandClick={() => toggle('senderName')}
        />
        <Collapse in={openPanel === 'senderName'} unmountOnExit>
          <Box pt={2}>
            <SenderNamePanel />
          </Box>
        </Collapse>
      </Box>

      {/* Users and Permissions Section */}
      <Box py="20px" sx={{ borderBottom: `1px solid ${BORDER}` }}>
        <SectionHeader
          title="משתמשים והרשאות"
          icon={InfoIcon}
          isOpen={openPanel === 'usersPermissions'}
          onExpandClick={() => toggle('usersPermissions')}
        />
        <Collapse in={openPanel === 'usersPermissions'} unmountOnExit>
          <Box pt={3}>
            <UsersPermissionsTable />
          </Box>
        </Collapse>
      </Box>

      {/* Interface Status Section */}
      <Box py="20px" sx={{ borderBottom: `1px solid ${BORDER}` }}>
        <SectionHeader
          title="סטטוס ממשקים"
          icon={GreenUpload}
          isOpen={openPanel === 'interfaceStatus'}
          onExpandClick={() => toggle('interfaceStatus')}
        />
        <Collapse in={openPanel === 'interfaceStatus'} unmountOnExit>
          <Box pt={3}>
            <InterfaceStatusTable />
          </Box>
        </Collapse>
      </Box>

      {/* Alert Indication Settings */}
      <Box py="20px" sx={{ borderBottom: `1px solid ${BORDER}` }}>
        <SectionHeader
          title="תצוגת חיווי התראות"
          icon={AlertBellIcon}
          isOpen={openPanel === 'alertIndication'}
          onExpandClick={() => toggle('alertIndication')}
        />
        <Collapse in={openPanel === 'alertIndication'} unmountOnExit>
          <Box pt={2} pr={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showMutedAlertIndication}
                  onChange={(e) => setShowMutedAlertIndication(e.target.checked)}
                  sx={{
                    color: '#3B82F6',
                    '&.Mui-checked': { color: '#3B82F6' },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    color: isDarkMode ? '#BBD7FF' : '#003F61',
                    fontFamily: 'Rubik, sans-serif',
                    fontSize: '15px',
                    fontWeight: 400,
                  }}
                >
                  הצגת חיווי התראות מושתקות
                </Typography>
              }
              sx={{ ml: 0 }}
            />
          </Box>
        </Collapse>
      </Box>

      {/* Update Consumers Info Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 8 }}>
        <Box
          onClick={() => setIsUpdateModalOpen(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            border: `1px solid ${isDarkMode ? '#374151' : '#E4E7EB'}`,
            borderRadius: '10px',
            px: 4,
            py: 1.2,
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: isDarkMode ? 'rgba(114, 164, 235, 0.1)' : 'rgba(114, 164, 235, 0.05)',
              borderColor: '#72A4EB',
            }
          }}
        >
          <img 
            src={ConsumersIcon} 
            alt="" 
            style={{ 
              width: 22, 
              height: 22, 
              filter: isDarkMode ? 'brightness(0) invert(1) opacity(0.7)' : 'none' 
            }} 
          />
          <Typography
            sx={{
              color: isDarkMode ? '#BBD7FF' : '#003F61',
              fontFamily: 'Rubik',
              fontSize: '16px',
              fontWeight: 400,
            }}
          >
            עדכון פרטי צרכנים במערכת
          </Typography>
        </Box>
      </Box>

      <UpdateConsumersModal 
        open={isUpdateModalOpen} 
        onClose={() => setIsUpdateModalOpen(false)} 
      />
    </Box>
  )
}

export default Organization
